import React, { useEffect, useState } from "react";

type Theme = "light" | "dark";

function readTheme(): Theme {
    const saved = localStorage.getItem("theme") as Theme | null;
    if (saved === "dark" || saved === "light") return saved;
    return document.documentElement.classList.contains("dark") ? "dark" : "light";
}

function writeTheme(theme: Theme) {
    document.documentElement.classList.toggle("dark", theme === "dark");
    localStorage.setItem("theme", theme);
}

function FlameIcon(props: React.SVGProps<SVGSVGElement>) {
    const { className, ...rest } = props;
    return (
        <svg viewBox="0 0 24 24" className={className} aria-hidden="true" {...rest}>
            <path
                fill="currentColor"
                d="M12.7 2.2c.2 2.3-.8 3.8-2 5.1-1.1 1.2-2.1 2.3-2.1 4.1 0 2.2 1.7 4 3.9 4s3.9-1.8 3.9-4c0-1.5-.6-2.5-1.4-3.4-.5-.6-1.1-1.3-1.3-2.2 3 1.5 5.2 4.6 5.2 8.3 0 5-4.1 9-9.1 9s-9.1-4-9.1-9c0-4.8 3.6-8 6-10 .7-.6 1.4-1.2 2-1.8z"
            />
        </svg>
    );
}

function DropletsIcon(props: React.SVGProps<SVGSVGElement>) {
    const { className, ...rest } = props;
    return (
        <svg viewBox="0 0 24 24" className={className} aria-hidden="true" {...rest}>
            <path
                fill="currentColor"
                d="M8.6 3.2c-1.9 2.7-3.7 4.9-3.7 7.4 0 2.4 1.9 4.3 4.3 4.3 2.4 0 4.3-1.9 4.3-4.3 0-2.5-1.9-4.7-3.7-7.4a.7.7 0 0 0-1.2 0zm7.9 1.8c-1.4 2-2.7 3.5-2.7 5.4 0 1.8 1.5 3.2 3.3 3.2 1.8 0 3.3-1.4 3.3-3.2 0-1.9-1.3-3.4-2.7-5.4a.7.7 0 0 0-1.2 0z"
            />
        </svg>
    );
}

export default function ThemeToggle() {
    const [theme, setTheme] = useState<Theme>(() => readTheme());

    const isDark = theme === "dark";
    const toggle = () => {
        const next: Theme = isDark ? "light" : "dark";
        writeTheme(next);
        setTheme(next);
    };

    const W = 60;
    const H = 34;
    const PAD = 4;
    const THUMB = H - PAD * 2;
    const TRAVEL = W - PAD * 2 - THUMB;

    const ICON = "h-[14px] w-[14px]";

    return (
        <button
            type="button"
            onClick={toggle}
            role="switch"
            aria-checked={isDark}
            aria-label="Toggle theme"
            className="relative inline-flex shrink-0 rounded-full focus:outline-none"
            style={{
                width: W,
                height: H,
                padding: PAD,
                background:
                    "linear-gradient(180deg, rgba(255,255,255,.18) 0%, rgba(255,255,255,.06) 100%), #3b4156",
                border: "1px solid rgba(255,255,255,.18)",
                boxShadow:
                    "inset 0 10px 16px rgba(0,0,0,.42), inset 0 1px 0 rgba(255,255,255,.10)",
            }}
        >
            <span
                aria-hidden="true"
                className="absolute inset-[2px] rounded-full"
                style={{
                    boxShadow:
                        "inset 0 0 0 1px rgba(255,255,255,.10), inset 0 -10px 14px rgba(0,0,0,.18)",
                }}
            />

            <span
                className="absolute grid place-items-center rounded-full transition-transform duration-200 ease-out"
                style={{
                    width: THUMB,
                    height: THUMB,
                    left: PAD,
                    top: PAD,
                    transform: `translateX(${isDark ? TRAVEL : 0}px)`,
                    background:
                        "radial-gradient(120% 120% at 30% 30%, rgba(255,255,255,.20) 0%, rgba(255,255,255,.08) 40%, rgba(0,0,0,.24) 100%), #4f566f",
                    border: "1px solid rgba(255,255,255,.14)",
                    boxShadow:
                        "0 8px 14px rgba(0,0,0,.55), inset 0 1px 0 rgba(255,255,255,.18)",
                }}
            >
                {isDark ? (
                    <DropletsIcon className={ICON} style={{ color: "rgba(255,255,255,.92)" }} />
                ) : (
                    <FlameIcon className={ICON} style={{ color: "rgb(var(--primary))" }} />
                )}
            </span>
        </button>
    );
}
